import { BaseWidgetFormFields } from 'components/consumer-page-widgets/BaseWidgetFormFields';
import { Button, Typography } from 'ui';
import { Form, Formik } from 'formik';
import {
  GridWidgetFields,
  GridWidgetFormFields,
} from 'components/consumer-page-widgets/grid-widget/GridWidgetFormFields';
import { StoreType } from 'utils/connect';
import { consumer } from '@tock/coretypes';
import { fetchAllNamedConfigurations } from 'actions/ConsumerPageActions';
import {
  getBaseFormFieldsInitialValues,
  useFetchAllCollectionConfigurations,
  useUpdateNamedConfigurations,
} from 'components/consumer-page-widgets/widgetFormUtils';
import { useAppDispatch } from 'state/hooks';
import { useSelector } from 'react-redux';
import {
  widgetFormButtonContainer,
  widgetFormRoot,
} from 'components/consumer-page-widgets/widgetFormCssDefs';
import React, { FunctionComponent, useEffect } from 'react';

interface UpdateGridWidgetFormProps {
  selectedWidgetToEdit?: consumer.NamedConfiguration;
  onCompleteOrCancel(): void;
}

function getInitialGridValues(
  grid: consumer.IGridWidgetConfig,
  selectedWidgetToEdit?: consumer.NamedConfiguration
): GridWidgetFields {
  const dataType = grid.businessConfig ? 'business' : grid.experienceConfig ? 'experience' : '';
  return {
    ...getBaseFormFieldsInitialValues({ widgetToEdit: selectedWidgetToEdit }),
    title: grid.title || '',
    subtitle: grid.subtitle || '',
    linkHref: grid.linkHref || '',
    linkText: grid.linkText || '',
    dataType,
    configId:
      (dataType === 'business'
        ? grid.businessConfig?.dataWidgetConfigId
        : grid.experienceConfig?.dataWidgetConfigId) || -1,
    pageSize:
      (dataType === 'business'
        ? grid.businessConfig?.searchConfigParams?.size
        : grid.experienceConfig?.searchConfigParams?.size) || 25,
  };
}

export const UpdateGridWidgetForm: FunctionComponent<
  React.PropsWithChildren<UpdateGridWidgetFormProps>
> = ({ selectedWidgetToEdit, onCompleteOrCancel }) => {
  const activeCollectionConfigs = useSelector(
    (state: StoreType) => state.consumerPage.collectionDataConfigurations
  ) as consumer.CollectionDataConfiguration[];
  const fetchAllCollectionConfigurations = useFetchAllCollectionConfigurations();

  useEffect(() => {
    fetchAllCollectionConfigurations();
  }, [fetchAllCollectionConfigurations]);

  const updateNamedConfig = useUpdateNamedConfigurations();

  const dispatch = useAppDispatch();

  React.useEffect(() => {
    dispatch(fetchAllNamedConfigurations());
  }, [dispatch]);

  if (!selectedWidgetToEdit?.gridWidgetConfig) {
    return null;
  }

  const initialValues = getInitialGridValues(
    selectedWidgetToEdit.gridWidgetConfig,
    selectedWidgetToEdit
  );

  const getGridDataConfig = ({ dataType, configId, pageSize }: GridWidgetFields) => {
    switch (dataType) {
      case 'business':
        return {
          businessConfig: {
            ...selectedWidgetToEdit.gridWidgetConfig?.businessConfig,
            dataWidgetConfigId: configId,
            searchConfigParams: {
              ...selectedWidgetToEdit.gridWidgetConfig?.businessConfig?.searchConfigParams,
              size: pageSize,
            },
          },
          experienceConfig: undefined,
        };
      case 'experience':
        return {
          businessConfig: undefined,
          experienceConfig: {
            ...selectedWidgetToEdit.gridWidgetConfig?.experienceConfig,
            dataWidgetConfigId: configId,
            searchConfigParams: {
              ...selectedWidgetToEdit.gridWidgetConfig?.experienceConfig?.searchConfigParams,
              size: pageSize,
            },
          },
        };

      default:
        return {};
    }
  };

  const handleSubmit = (values: GridWidgetFields) => {
    const { title, name, subtitle, linkText, linkHref } = values;
    return updateNamedConfig({
      ...selectedWidgetToEdit,
      name,
      gridWidgetConfig: {
        ...selectedWidgetToEdit.gridWidgetConfig,
        title,
        subtitle,
        linkText,
        linkHref,
        displayType: selectedWidgetToEdit.gridWidgetConfig?.displayType || 'GRID',
        ...getGridDataConfig(values),
      },
    }).then(onCompleteOrCancel);
  };

  return (
    <>
      <Typography variant="heading1Bold">Editing widget: {selectedWidgetToEdit.name}</Typography>
      <Formik<GridWidgetFields>
        enableReinitialize
        initialValues={{
          ...initialValues,
        }}
        onSubmit={handleSubmit}
      >
        <Form css={widgetFormRoot}>
          <BaseWidgetFormFields />
          {/* defensive check */}
          {selectedWidgetToEdit.gridWidgetConfig.displayType && (
            <GridWidgetFormFields
              collectionDataConfigurations={activeCollectionConfigs}
              displayMode={selectedWidgetToEdit.gridWidgetConfig.displayType}
            />
          )}
          <div css={widgetFormButtonContainer}>
            <Button type="submit">Submit</Button>
            <Button onClick={onCompleteOrCancel} type="button" variant="outlined">
              Cancel
            </Button>
          </div>
        </Form>
      </Formik>
    </>
  );
};
